/**
 * Button Component - Reusable button with variants
 *
 * Variants:
 * - primary: Main brand color button
 * - secondary: Secondary action button
 * - outline: Outlined button
 * - ghost: Transparent background with border on hover
 * - danger: Destructive action
 * - success: Success action
 *
 * Sizes:
 * - xs: Extra small
 * - sm: Small
 * - md: Medium (default)
 * - lg: Large
 * - xl: Extra large
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';

const buttonVariants = cva(
  // Base styles - applied to all variants
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 focus-visible:ring-primary-500 shadow-sm',
        secondary: 'bg-secondary-500 text-white hover:bg-secondary-600 active:bg-secondary-700 focus-visible:ring-secondary-500 shadow-sm',
        outline: 'border-2 border-primary-500 text-primary-500 hover:bg-primary-50 active:bg-primary-100 focus-visible:ring-primary-500',
        ghost: 'text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 focus-visible:ring-neutral-500',
        danger: 'bg-error-500 text-white hover:bg-error-600 active:bg-error-700 focus-visible:ring-error-500 shadow-sm',
        success: 'bg-success-500 text-white hover:bg-success-600 active:bg-success-700 focus-visible:ring-success-500 shadow-sm',
        warning: 'bg-warning-500 text-white hover:bg-warning-600 active:bg-warning-700 focus-visible:ring-warning-500 shadow-sm',
        link: 'text-primary-500 underline-offset-4 hover:underline focus-visible:ring-primary-500',
      },
      size: {
        xs: 'h-7 px-2 text-xs',
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 text-sm',
        lg: 'h-10 px-5 text-base',
        xl: 'h-12 px-6 text-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  href?: string;
}

/**
 * Button Component
 *
 * @example
 * ```tsx
 * <Button>Click me</Button>
 * <Button variant="secondary">Secondary</Button>
 * <Button variant="outline" size="lg">Large Outline</Button>
 * <Button variant="danger" leftIcon={<TrashIcon />}>Delete</Button>
 * <Button loading>Loading...</Button>
 * <Button href="/path">As Link</Button>
 * ```
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, loading, leftIcon, rightIcon, children, disabled, href, ...props }, ref) => {
    // If href is provided, render as a link
    if (href) {
      return (
        <a
          href={href}
          className={cn(buttonVariants({ variant, size, fullWidth, className }))}
          ref={ref as any}
          {...(props as any)}
        >
          {loading && <ButtonSpinner />}
          {!loading && leftIcon}
          {leftIcon && <span className="ml-1">{children}</span>}
          {!leftIcon && !rightIcon && children}
          {!loading && rightIcon && children}
          {!loading && rightIcon}
        </a>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <ButtonSpinner />}
        {!loading && leftIcon}
        {leftIcon && <span className="ml-1">{children}</span>}
        {!leftIcon && !rightIcon && children}
        {!loading && rightIcon && children}
        {!loading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';

// Loading spinner component for buttons
const ButtonSpinner = () => (
  <svg
    className="h-4 w-4 animate-spin"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export default Button;
